import React, { useState, useEffect, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import {
  accountsApi,
  executionsApi,
  tradingApi,
  TradingAccount,
  ExecutionSummary,
  CompletedTrade,
  ExecutionOrder,
  ManualExecutionResult,
  DividendTracking
} from '../services/api';
import { formatCurrency, formatPercent } from '../utils/format';

type TabType = 'trades' | 'orders' | 'summary' | 'dividends';
type DateRangePreset = 'ytd' | '1y' | 'all' | 'custom';
type ChartUnit = '$' | '%';

const Trading: React.FC = () => {
  const { token } = useAuth();
  const [accounts, setAccounts] = useState<TradingAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('trades');

  // Data states
  const [trades, setTrades] = useState<CompletedTrade[]>([]);
  const [orders, setOrders] = useState<ExecutionOrder[]>([]);
  const [summary, setSummary] = useState<ExecutionSummary | null>(null);
  const [chartTrades, setChartTrades] = useState<CompletedTrade[]>([]);
  const [dividendTracking, setDividendTracking] = useState<DividendTracking[]>([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tradeFilter, setTradeFilter] = useState<'all' | 'open' | 'closed'>('all');
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<ManualExecutionResult | null>(null);
  const [closingTradeId, setClosingTradeId] = useState<number | null>(null);

  // Date range & chart states
  const [dateRange, setDateRange] = useState<DateRangePreset>('all');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [chartUnit, setChartUnit] = useState<ChartUnit>('$');

  // Load accounts on mount
  useEffect(() => {
    if (!token) return;
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Stable key that only changes when the effective range is complete
  const effectiveDateRange = dateRange === 'custom'
    ? (customStartDate && customEndDate ? `${customStartDate}_${customEndDate}` : null)
    : dateRange;

  // Load data when account or filters change
  useEffect(() => {
    if (!token || !selectedAccountId) return;
    if (dateRange === 'custom' && (!customStartDate || !customEndDate)) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedAccountId, activeTab, tradeFilter, effectiveDateRange]);

  const loadAccounts = async () => {
    if (!token) return;

    try {
      const data = await accountsApi.listAccounts(token);
      setAccounts(data);
      if (data.length > 0) {
        // Select default account or first account
        const defaultAccount = data.find(a => a.is_default) || data[0];
        setSelectedAccountId(defaultAccount.id);
      }
    } catch (err: any) {
      console.error('Failed to load accounts:', err);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const getDateRangeDates = (): { startDate?: string; endDate?: string } => {
    const now = new Date();
    switch (dateRange) {
      case 'ytd':
        return { startDate: `${now.getFullYear()}-01-01` };
      case '1y': {
        const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        return { startDate: oneYearAgo.toISOString().split('T')[0] };
      }
      case 'custom':
        return {
          startDate: customStartDate || undefined,
          endDate: customEndDate || undefined
        };
      case 'all':
      default:
        return {};
    }
  };

  const getDateRangeLabel = (): string | null => {
    const { startDate, endDate } = getDateRangeDates();
    if (!startDate && !endDate) return null;
    const fmt = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString();
    if (startDate && endDate) return `${fmt(startDate)} – ${fmt(endDate)}`;
    if (startDate) return `Since ${fmt(startDate)}`;
    if (endDate) return `Through ${fmt(endDate)}`;
    return null;
  };

  const loadData = async () => {
    if (!token || !selectedAccountId) return;

    setLoading(true);
    setError(null);

    try {
      if (activeTab === 'trades') {
        const statusFilter = tradeFilter === 'all' ? undefined : tradeFilter;
        const data = await executionsApi.getTradeHistory(selectedAccountId, token, 90, statusFilter);
        setTrades(data);
      } else if (activeTab === 'orders') {
        const data = await executionsApi.getOrderHistory(selectedAccountId, token, 30);
        setOrders(data);
      } else if (activeTab === 'summary') {
        const { startDate, endDate } = getDateRangeDates();
        const [summaryData, tradesData] = await Promise.all([
          executionsApi.getExecutionSummary(selectedAccountId, token, startDate, endDate),
          executionsApi.getTradeHistory(selectedAccountId, token, 365 * 5, 'closed')
        ]);
        setSummary(summaryData);
        setChartTrades(tradesData);
      } else if (activeTab === 'dividends') {
        const data = await executionsApi.getDividendTracking(selectedAccountId, token);
        setDividendTracking(data);
      }
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleManualExecution = async () => {
    if (!token || !selectedAccountId) return;

    if (!window.confirm('This will execute today\'s scheduled trades. Continue?')) return;

    setExecuting(true);
    setExecutionResult(null);
    setError(null);

    try {
      const result = await executionsApi.triggerManualExecution(selectedAccountId, token);
      setExecutionResult(result);
      // Reload data after execution
      await loadData();
    } catch (err: any) {
      console.error('Execution failed:', err);
      setError(err.response?.data?.detail || 'Failed to execute trades');
    } finally {
      setExecuting(false);
    }
  };

  const handleCloseTrade = async (trade: CompletedTrade) => {
    if (!token || !selectedAccountId) return;
    if (!window.confirm(`Force-close ${trade.symbol} position (${trade.quantity} shares)? This will place a market sell order.`)) return;

    setClosingTradeId(trade.id);
    setError(null);

    try {
      await tradingApi.closePosition(selectedAccountId, trade.id, token);
      await loadData();
    } catch (err: any) {
      console.error('Failed to close position:', err);
      setError(err.response?.data?.detail || 'Failed to close position');
    } finally {
      setClosingTradeId(null);
    }
  };


  const formatDate = (dateStr: string | undefined | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getPnlColor = (value: number | undefined | null): string => {
    if (value === undefined || value === null || value === 0) return '#6b7280';
    return value > 0 ? '#10b981' : '#ef4444';
  };

  // Filter chart trades based on selected date range
  const filteredChartTrades = useMemo(() => {
    if (!chartTrades.length) return [];

    const { startDate, endDate } = getDateRangeDates();
    const start = startDate ? new Date(startDate + 'T00:00:00') : new Date(0);
    const end = endDate ? new Date(endDate + 'T23:59:59') : new Date();

    return chartTrades
      .filter(trade => {
        if (!trade.exit_date) return false;
        const exitDate = new Date(trade.exit_date);
        return exitDate >= start && exitDate <= end;
      })
      .sort((a, b) => new Date(a.exit_date!).getTime() - new Date(b.exit_date!).getTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartTrades, effectiveDateRange]);

  // Generate ECharts options
  const chartOptions = useMemo(() => {
    if (!filteredChartTrades.length) return null;

    const data = filteredChartTrades.map(trade => {
      const value = chartUnit === '$' ? (trade.total_pnl || 0) : (trade.return_pct || 0);
      return {
        date: trade.exit_date!,
        value,
        symbol: trade.symbol,
        color: value >= 0 ? '#10b981' : '#ef4444'
      };
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const item = params[0];
          if (!item) return '';
          const trade = filteredChartTrades[item.dataIndex];
          const value = chartUnit === '$'
            ? formatCurrency(trade.total_pnl)
            : formatPercent(trade.return_pct);
          return `<strong>${trade.symbol}</strong><br/>
                  Date: ${new Date(trade.exit_date!).toLocaleDateString()}<br/>
                  Return: ${value}`;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: data.map(d => new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
        axisLabel: {
          rotate: 45,
          fontSize: 10,
          color: '#6b7280'
        },
        axisLine: { lineStyle: { color: '#e5e7eb' } }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => chartUnit === '$' ? `$${value.toFixed(0)}` : `${value.toFixed(1)}%`,
          color: '#6b7280'
        },
        splitLine: { lineStyle: { color: '#f3f4f6' } }
      },
      series: [{
        type: 'bar',
        data: data.map(d => ({
          value: d.value,
          itemStyle: { color: d.color }
        })),
        barMaxWidth: 30
      }]
    };
  }, [filteredChartTrades, chartUnit]);

  if (accounts.length === 0 && !loading) {
    return (
      <Layout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <div style={{
            textAlign: 'center',
            maxWidth: '600px',
            padding: '40px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>💼</div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1e293b',
              marginBottom: '16px'
            }}>
              No Trading Accounts
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              lineHeight: '1.6',
              marginBottom: '24px'
            }}>
              You need to connect a trading account before you can view execution history.
            </p>
            <a
              href="/accounts"
              style={{
                display: 'inline-block',
                padding: '12px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '500'
              }}
            >
              Add Trading Account
            </a>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1e293b',
              margin: '0 0 8px 0'
            }}>
              Trading Activity
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              margin: 0
            }}>
              View execution history, trade performance, and P&L breakdown
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Account Selector */}
            {accounts.length > 1 && (
              <select
                value={selectedAccountId || ''}
                onChange={(e) => setSelectedAccountId(Number(e.target.value))}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.account_type})
                  </option>
                ))}
              </select>
            )}

            {/* Manual Execute Button */}
            <button
              onClick={handleManualExecution}
              disabled={executing || !selectedAccountId}
              title="Manually trigger today's scheduled dividend capture trades for the selected account"
              style={{
                padding: '10px 20px',
                backgroundColor: executing ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: executing ? 'not-allowed' : 'pointer'
              }}
            >
              {executing ? 'Executing...' : 'Run Now'}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            padding: '12px 16px',
            color: '#b91c1c'
          }}>
            {error}
          </div>
        )}

        {/* Execution Result */}
        {executionResult && (
          <div style={{
            backgroundColor: executionResult.status === 'success' ? '#dcfce7' : '#fef3c7',
            border: `1px solid ${executionResult.status === 'success' ? '#10b981' : '#f59e0b'}`,
            borderRadius: '6px',
            padding: '16px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start'
            }}>
              <div>
                <div style={{
                  fontWeight: '600',
                  color: executionResult.status === 'success' ? '#166534' : '#92400e',
                  marginBottom: '8px'
                }}>
                  Execution {executionResult.status === 'success' ? 'Complete' : 'Completed with Issues'}
                </div>
                <div style={{ fontSize: '14px', color: '#374151' }}>
                  <div>Orders placed: {executionResult.orders_placed}</div>
                  <div>Successful: {executionResult.successful_orders}</div>
                  {executionResult.failed_orders > 0 && (
                    <div style={{ color: '#dc2626' }}>Failed: {executionResult.failed_orders}</div>
                  )}
                  {executionResult.executed_symbols.length > 0 && (
                    <div>Symbols: {executionResult.executed_symbols.join(', ')}</div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setExecutionResult(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '0',
          borderBottom: '2px solid #e5e7eb'
        }}>
          {[
            { id: 'trades' as TabType, label: 'Trades' },
            { id: 'orders' as TabType, label: 'Orders' },
            { id: 'summary' as TabType, label: 'Performance' },
            { id: 'dividends' as TabType, label: 'Dividends' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: 'transparent',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
                borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                marginBottom: '-2px'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px'
          }}>
            <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading...</div>
          </div>
        ) : (
          <>
            {/* Trades Tab */}
            {activeTab === 'trades' && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                {/* Trades Header */}
                <div style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#1e293b',
                    margin: 0
                  }}>
                    Dividend Capture Trades
                  </h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {(['all', 'open', 'closed'] as const).map(filter => (
                      <button
                        key={filter}
                        onClick={() => setTradeFilter(filter)}
                        style={{
                          padding: '6px 12px',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          backgroundColor: tradeFilter === filter ? '#3b82f6' : '#f3f4f6',
                          color: tradeFilter === filter ? 'white' : '#374151'
                        }}
                      >
                        {filter.charAt(0).toUpperCase() + filter.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Trades Table */}
                {trades.length === 0 ? (
                  <div style={{
                    padding: '60px 24px',
                    textAlign: 'center',
                    color: '#9ca3af'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                    <p style={{ fontSize: '16px', marginBottom: '8px' }}>No trades found</p>
                    <p style={{ fontSize: '14px' }}>Trades will appear here once the strategy executes</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '14px'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Symbol</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Shares</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Entry Date</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Entry Price</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Entry Cost</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Exit Date</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Exit Price</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Price P&L</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Dividend</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Total P&L</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Status</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Sizing</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trades.map((trade) => (
                          <tr key={trade.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>
                              {trade.symbol}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              {trade.quantity}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                              {formatDate(trade.entry_date)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              {formatCurrency(trade.entry_price)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              {formatCurrency(trade.entry_cost)}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                              {formatDate(trade.exit_date)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              {formatCurrency(trade.exit_price)}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              textAlign: 'right',
                              fontWeight: '500',
                              color: getPnlColor(trade.price_pnl)
                            }}>
                              {formatCurrency(trade.price_pnl)}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              textAlign: 'right',
                              fontWeight: '500',
                              color: '#10b981'
                            }}>
                              {trade.dividend_captured ? formatCurrency(trade.total_dividend) : '-'}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              textAlign: 'right',
                              fontWeight: '600',
                              color: getPnlColor(trade.total_pnl)
                            }}>
                              {formatCurrency(trade.total_pnl)}
                              {trade.return_pct !== undefined && trade.return_pct !== null && (
                                <div style={{ fontSize: '12px', fontWeight: '400' }}>
                                  ({formatPercent(trade.return_pct)})
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: trade.status === 'open' ? '#fef3c7' : '#dcfce7',
                                color: trade.status === 'open' ? '#92400e' : '#166534',
                                textTransform: 'capitalize'
                              }}>
                                {trade.status}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              {trade.sizing_constraint === 'volume' ? (
                                <span style={{
                                  padding: '3px 7px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  backgroundColor: '#fef3c7',
                                  color: '#92400e',
                                }} title="Position size was limited by trading volume">
                                  Vol limited
                                </span>
                              ) : trade.sizing_constraint === 'no_adv' ? (
                                <span style={{
                                  padding: '3px 7px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  backgroundColor: '#fee2e2',
                                  color: '#dc2626',
                                }} title="No volume data available — used $10k fallback">
                                  No ADV
                                </span>
                              ) : trade.sizing_constraint ? (
                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                  {trade.sizing_constraint}
                                </span>
                              ) : (
                                <span style={{ fontSize: '11px', color: '#d1d5db' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              {trade.status === 'open' && (
                                <button
                                  onClick={() => handleCloseTrade(trade)}
                                  disabled={closingTradeId === trade.id}
                                  style={{
                                    padding: '4px 10px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    cursor: closingTradeId === trade.id ? 'not-allowed' : 'pointer',
                                    backgroundColor: '#fee2e2',
                                    color: '#dc2626',
                                    opacity: closingTradeId === trade.id ? 0.6 : 1
                                  }}
                                >
                                  {closingTradeId === trade.id ? 'Closing...' : 'Close'}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#1e293b',
                    margin: 0
                  }}>
                    Order History (Last 30 Days)
                  </h3>
                </div>

                {orders.length === 0 ? (
                  <div style={{
                    padding: '60px 24px',
                    textAlign: 'center',
                    color: '#9ca3af'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                    <p style={{ fontSize: '16px', marginBottom: '8px' }}>No orders found</p>
                    <p style={{ fontSize: '14px' }}>Orders will appear here once the strategy executes</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '14px'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Symbol</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Side</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Type</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Quantity</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Filled</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Fill Price</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Total Value</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Submitted</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <tr key={order.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>
                              {order.symbol}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: order.side === 'buy' ? '#dcfce7' : '#fee2e2',
                                color: order.side === 'buy' ? '#166534' : '#991b1b',
                                textTransform: 'uppercase'
                              }}>
                                {order.side}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', color: '#6b7280', textTransform: 'uppercase' }}>
                              {order.order_type}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              {order.quantity}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              {order.filled_quantity}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              {formatCurrency(order.filled_price)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              {formatCurrency(order.total_value)}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                              {formatDate(order.submitted_at)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor:
                                  order.status === 'filled' ? '#dcfce7' :
                                  order.status === 'cancelled' ? '#fee2e2' :
                                  order.status === 'submitted' ? '#fef3c7' : '#f3f4f6',
                                color:
                                  order.status === 'filled' ? '#166534' :
                                  order.status === 'cancelled' ? '#991b1b' :
                                  order.status === 'submitted' ? '#92400e' : '#374151',
                                textTransform: 'capitalize'
                              }}>
                                {order.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Performance Tab */}
            {activeTab === 'summary' && summary && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Date Range Selector */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  padding: '16px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Date Range</span>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {(['ytd', '1y', 'all', 'custom'] as DateRangePreset[]).map(preset => (
                      <button
                        key={preset}
                        onClick={() => setDateRange(preset)}
                        style={{
                          padding: '6px 14px',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          backgroundColor: dateRange === preset ? '#3b82f6' : '#f3f4f6',
                          color: dateRange === preset ? 'white' : '#374151'
                        }}
                      >
                        {preset === 'ytd' ? 'YTD' : preset === '1y' ? '1Y' : preset === 'all' ? 'All' : 'Custom'}
                      </button>
                    ))}
                  </div>
                  {dateRange === 'custom' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={e => setCustomStartDate(e.target.value)}
                        style={{
                          padding: '5px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '13px',
                          color: '#374151'
                        }}
                      />
                      <span style={{ color: '#9ca3af' }}>to</span>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={e => setCustomEndDate(e.target.value)}
                        style={{
                          padding: '5px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '13px',
                          color: '#374151'
                        }}
                      />
                    </div>
                  )}
                  {getDateRangeLabel() && (
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      {getDateRangeLabel()}
                    </span>
                  )}
                </div>

                {/* Top Row: Chart and Returns */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr',
                  gap: '24px'
                }}>
                  {/* Performance Chart */}
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    padding: '24px',
                    minHeight: '280px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px'
                    }}>
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#6b7280',
                        margin: 0
                      }}>
                        Trade Returns
                      </h3>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {(['$', '%'] as ChartUnit[]).map(unit => (
                          <button
                            key={unit}
                            onClick={() => setChartUnit(unit)}
                            style={{
                              padding: '4px 10px',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              backgroundColor: chartUnit === unit ? '#3b82f6' : '#f3f4f6',
                              color: chartUnit === unit ? 'white' : '#374151'
                            }}
                          >
                            {unit}
                          </button>
                        ))}
                      </div>
                    </div>
                    {chartOptions ? (
                      <ReactECharts
                        option={chartOptions}
                        style={{ height: '200px' }}
                        opts={{ renderer: 'svg' }}
                      />
                    ) : (
                      <div style={{
                        height: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px',
                        border: '1px dashed #d1d5db'
                      }}>
                        <span style={{ color: '#9ca3af', fontSize: '14px' }}>
                          No closed trades in this period
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Returns Tile */}
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    padding: '24px'
                  }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#6b7280',
                      margin: '0 0 20px 0'
                    }}>
                      Returns
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>All Time</span>
                        <span style={{
                          fontWeight: '600',
                          color: getPnlColor(summary.all_time_pnl)
                        }}>
                          {formatCurrency(summary.all_time_pnl)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>YTD</span>
                        <span style={{
                          fontWeight: '600',
                          color: getPnlColor(summary.ytd_pnl)
                        }}>
                          {formatCurrency(summary.ytd_pnl)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>1 Year</span>
                        <span style={{
                          fontWeight: '600',
                          color: getPnlColor(summary.one_year_pnl)
                        }}>
                          {formatCurrency(summary.one_year_pnl)}
                        </span>
                      </div>
                      {dateRange !== 'all' && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          backgroundColor: '#f0f9ff',
                          margin: '0 -12px',
                          padding: '8px 12px',
                          borderRadius: '6px'
                        }}>
                          <span style={{ color: '#3b82f6', fontWeight: '500' }}>
                            {dateRange === 'ytd' ? 'YTD' : dateRange === '1y' ? '1 Year' : getDateRangeLabel() || 'Selected'}
                          </span>
                          <span style={{
                            fontWeight: '600',
                            color: getPnlColor(summary.total_pnl)
                          }}>
                            {formatCurrency(summary.total_pnl)}
                          </span>
                        </div>
                      )}
                      <div style={{
                        borderTop: '1px solid #e5e7eb',
                        paddingTop: '16px',
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <span style={{ color: '#6b7280' }}>Avg Return</span>
                        <span style={{
                          fontWeight: '600',
                          color: getPnlColor(summary.avg_return_pct)
                        }}>
                          {formatPercent(summary.avg_return_pct)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Row: Three Metric Tiles */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '24px'
                }}>
                {/* Trade Counts */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  padding: '24px'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#6b7280',
                    margin: '0 0 20px 0'
                  }}>
                    Trade Activity
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Total Trades</span>
                      <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '18px' }}>
                        {summary.total_trades}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Open Positions</span>
                      <span style={{ fontWeight: '600', color: '#f59e0b' }}>
                        {summary.open_trades}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Closed Trades</span>
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>
                        {summary.closed_trades}
                      </span>
                    </div>
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Winning</span>
                      <span style={{ fontWeight: '600', color: '#10b981' }}>
                        {summary.winning_trades}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Losing</span>
                      <span style={{ fontWeight: '600', color: '#ef4444' }}>
                        {summary.losing_trades}
                      </span>
                    </div>
                  </div>
                </div>

                {/* P&L Breakdown */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  padding: '24px'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#6b7280',
                    margin: '0 0 20px 0'
                  }}>
                    P&L Breakdown
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Price P&L</span>
                      <span style={{ fontWeight: '600', color: getPnlColor(summary.total_price_pnl) }}>
                        {formatCurrency(summary.total_price_pnl)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#6b7280' }}>
                        Dividend Income
                        {summary.dividends_are_estimated && (
                          <span style={{
                            fontSize: '10px',
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            marginLeft: '6px',
                            fontWeight: '500'
                          }}>
                            EST
                          </span>
                        )}
                        {!summary.dividends_are_estimated && summary.total_dividend_verified > 0 && (
                          <span style={{
                            fontSize: '10px',
                            backgroundColor: '#d1fae5',
                            color: '#065f46',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            marginLeft: '6px',
                            fontWeight: '500'
                          }}>
                            VERIFIED
                          </span>
                        )}
                      </span>
                      <span style={{ fontWeight: '600', color: '#10b981' }}>
                        {formatCurrency(summary.total_dividend_income)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Total Fees</span>
                      <span style={{ fontWeight: '600', color: '#ef4444' }}>
                        -{formatCurrency(summary.total_fees)}
                      </span>
                    </div>
                    <div style={{
                      borderTop: '2px solid #e5e7eb',
                      paddingTop: '16px',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>Total P&L</span>
                      <span style={{
                        fontWeight: '700',
                        fontSize: '20px',
                        color: getPnlColor(summary.total_pnl)
                      }}>
                        {formatCurrency(summary.total_pnl)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Paper account dividend disclaimer */}
                {summary.dividends_are_estimated && (
                  <div style={{
                    gridColumn: '1 / -1',
                    backgroundColor: '#fffbeb',
                    border: '1px solid #f59e0b',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-start'
                  }}>
                    <span style={{ fontSize: '16px', flexShrink: 0 }}>&#9888;</span>
                    <div>
                      <span style={{ fontWeight: '600', color: '#92400e', fontSize: '13px' }}>
                        Paper Trading: Dividend income is estimated
                      </span>
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#92400e', lineHeight: '1.4' }}>
                        Alpaca paper trading does not simulate dividend payments. The {formatCurrency(summary.total_dividend_income)} in
                        dividend income shown above is based on expected payments from the dividend calendar.
                        In a live account, actual dividend payments would be verified against broker records.
                      </p>
                    </div>
                  </div>
                )}

                {/* Performance Metrics */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  padding: '24px'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#6b7280',
                    margin: '0 0 20px 0'
                  }}>
                    Performance Metrics
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Win Rate</span>
                      <span style={{
                        fontWeight: '600',
                        color: summary.win_rate >= 50 ? '#10b981' : '#ef4444'
                      }}>
                        {formatPercent(summary.win_rate, 1)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Avg Return</span>
                      <span style={{ fontWeight: '600', color: getPnlColor(summary.avg_return_pct) }}>
                        {formatPercent(summary.avg_return_pct)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Avg Holding Period</span>
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>
                        {summary.avg_holding_days.toFixed(1)} days
                      </span>
                    </div>
                    {summary.last_trade_date && (
                      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>Last Trade</span>
                        <span style={{ fontWeight: '500', color: '#6b7280' }}>
                          {formatDate(summary.last_trade_date)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                </div>
              </div>
            )}

            {activeTab === 'summary' && !summary && !loading && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                padding: '60px 24px',
                textAlign: 'center',
                color: '#9ca3af'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📈</div>
                <p style={{ fontSize: '16px', marginBottom: '8px' }}>No summary data available</p>
                <p style={{ fontSize: '14px' }}>Summary will be available once you have executed trades</p>
              </div>
            )}
          </>
        )}

        {/* Dividends Tab */}
        {activeTab === 'dividends' && (
          <>
            {dividendTracking.length > 0 ? (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                {/* Summary row */}
                <div style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  gap: '32px',
                  flexWrap: 'wrap'
                }}>
                  <div>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>Total Expected</span>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>
                      {formatCurrency(dividendTracking.reduce((sum, d) => sum + d.expected_total, 0))}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>Verified Paid</span>
                    <div style={{ fontWeight: '600', color: '#10b981' }}>
                      {formatCurrency(dividendTracking.filter(d => d.status === 'paid').reduce((sum, d) => sum + (d.actual_total || 0), 0))}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>Pending</span>
                    <div style={{ fontWeight: '600', color: '#f59e0b' }}>
                      {formatCurrency(dividendTracking.filter(d => d.status === 'pending').reduce((sum, d) => sum + d.expected_total, 0))}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>Positions</span>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>
                      {dividendTracking.length}
                    </div>
                  </div>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Symbol</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Shares</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Ex-Date</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Expected/Share</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Expected Total</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Actual Paid</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Status</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Payment Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dividendTracking.map((div) => {
                        const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
                          paid: { bg: '#d1fae5', text: '#065f46', label: 'Paid' },
                          pending: { bg: '#fef3c7', text: '#92400e', label: 'Pending' },
                          overdue: { bg: '#fee2e2', text: '#991b1b', label: 'Overdue' },
                          not_available: { bg: '#f3f4f6', text: '#6b7280', label: 'N/A (Paper)' }
                        };
                        const sc = statusConfig[div.status] || statusConfig.not_available;

                        return (
                          <tr key={div.position_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>
                              {div.symbol}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              {div.quantity.toLocaleString()}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#374151' }}>
                              {div.ex_dividend_date ? new Date(div.ex_dividend_date).toLocaleDateString() : '-'}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              ${div.expected_per_share.toFixed(4)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                              {formatCurrency(div.expected_total)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: div.actual_total != null ? '#10b981' : '#9ca3af' }}>
                              {div.actual_total != null ? formatCurrency(div.actual_total) : '-'}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                backgroundColor: sc.bg,
                                color: sc.text,
                                padding: '2px 8px',
                                borderRadius: '4px'
                              }}>
                                {sc.label}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', color: '#374151' }}>
                              {div.payment_date ? new Date(div.payment_date).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : !loading ? (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                padding: '60px 24px',
                textAlign: 'center',
                color: '#9ca3af'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#128176;</div>
                <p style={{ fontSize: '16px', marginBottom: '8px' }}>No dividend data available</p>
                <p style={{ fontSize: '14px' }}>Dividend tracking will appear after trades with expected dividends are executed</p>
              </div>
            ) : null}
          </>
        )}

        {/* Info Box */}
        <div style={{
          backgroundColor: '#eff6ff',
          border: '1px solid #3b82f6',
          borderRadius: '8px',
          padding: '16px 20px'
        }}>
          <h4 style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1e40af',
            margin: '0 0 8px 0'
          }}>
            About Dividend Capture Trading
          </h4>
          <p style={{
            margin: 0,
            fontSize: '13px',
            color: '#1e40af',
            lineHeight: '1.5'
          }}>
            The strategy buys stocks the day before their ex-dividend date and sells on the ex-dividend date.
            The goal is to capture the dividend payment. Price P&L may be negative due to the typical price drop
            on ex-dividend date, but the dividend income should offset this loss for a net profit.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Trading;
